import { Component, ViewChild } from '@angular/core';
import { BackendService } from '../../core/services/backend.service';
import { SessionService } from '../../core/services/session.service';
import { ActivatedRoute } from '@angular/router';
import { CustomDatePipe } from '../../core/pipes/date.pipe';
import { MenuBarComponent } from '../../core/components/menu-bar/menu-bar.component';
import { FooterComponent } from '../../core/components/footer/footer.component';
import { AdminNavComponent } from '../../core/components/admin-nav/admin-nav.component';
import { FormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { ToastrService } from 'ngx-toastr';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'app-order-details',
  imports: [CustomDatePipe, MenuBarComponent, FooterComponent, AdminNavComponent, FormsModule, NgSelectModule],
  templateUrl: './order-details.component.html',
  styleUrl: './order-details.component.scss'
})
export class OrderDetailsComponent {
  orderId: string = '';
  order: any;
  newComment: string = '';

  /** Options for the status dropdown (Wallio_OrderStatuses). */
  statuses: { id: string; title: string }[] = [];
  /**
   * 'New' is the status every order is created with — it is a starting point,
   * not something anyone sets, so it is kept out of the dropdown's options.
   */
  private static readonly INITIAL_STATUS_TITLE = 'New';
  /** Bound to the dropdown — the id of the currently selected status. */
  statusId: string | null = null;
  savingStatus = false;
  /** True while the take request is in flight, to block a double assignment. */
  takingOrder = false;

  /**
   * Needed to roll the selection back: ng-select holds its own view state, and
   * reassigning the one-way [ngModel] does not push a reverted value into it.
   */
  @ViewChild('statusSelect') statusSelect?: NgSelectComponent;

  constructor(
    private backendService: BackendService,
    private route: ActivatedRoute,
    private toastr: ToastrService,
    private session: SessionService,
  ) {
    //get order id from url using angular router
    this.orderId = this.route.snapshot.paramMap.get('id') || '';
  }

  ngOnInit(): void {
    this.backendService.getOrderById(this.orderId).subscribe(order => {
      this.order = order.data.order;
      this.statusId = this.order?.statusId ?? null;
    });
    this.backendService.getOrderStatuses().subscribe({
      next: (res) => {
        const all: { id: string; title: string }[] = res?.data?.statuses ?? [];
        this.statuses = all.filter(
          (s) => s.title !== OrderDetailsComponent.INITIAL_STATUS_TITLE,
        );
      },
      error: () => { this.statuses = []; },
    });
  }

  /**
   * Label shown in place of the dropdown while the order is unassigned — whoever
   * takes it owns moving it on from 'New'.
   */
  get currentStatusTitle(): string {
    return this.order?.status
      ?? this.statuses.find((s) => s.id === this.statusId)?.title
      ?? OrderDetailsComponent.INITIAL_STATUS_TITLE;
  }

  /**
   * Persists a new status. The dropdown moves immediately and is rolled back if
   * the request fails, so it never shows a status the backend did not accept.
   */
  public onStatusChange(statusId: string | null): void {
    if (!statusId || statusId === this.order?.statusId) return;
    // Belt and braces: the dropdown is not rendered while unassigned.
    if (!this.order?.assignedTo) return;

    const previousId = this.order?.statusId ?? null;
    this.statusId = statusId;
    this.savingStatus = true;

    this.backendService.updateOrderStatus(this.orderId, statusId)
      .pipe(finalize(() => { this.savingStatus = false; }))
      .subscribe({
        next: () => {
          this.order.statusId = statusId;
          this.order.status = this.statuses.find(s => s.id === statusId)?.title ?? this.order.status;
          this.toastr.success('Order status updated');
        },
        error: () => {
          this.statusId = previousId;
          this.statusSelect?.writeValue(previousId);
          this.toastr.error('Unable to update the order status');
        },
      });
  }

  public onEnterPress(event: any): void {
    // If Shift+Enter → allow newline
    if (event.shiftKey) return;

    // Prevent default "newline" behavior
    event.preventDefault();

    // Only call addComment if there's text
    if (this.newComment.trim()) {
      this.addComment();
    }
  }

  public addComment(): void {
    // Example logic
    const comment = {
      comment: this.newComment.trim(),
      date: new Date(),
      // The backend attributes the note to the signed-in admin; show the same
      // person here rather than a placeholder until the next reload.
      commentedBy: this.session.username() || 'You'
    };

    // Push to UI list (if needed)
    this.order.comments.push(comment);

    this.backendService.addComment(this.orderId, comment.comment).subscribe();

    // Clear input
    this.newComment = '';
  }

  /** Assigns the order to the signed-in admin. */
  public takeOrder(order: any): void {
    if (this.takingOrder) return;
    this.takingOrder = true;

    this.backendService.takeOrder(order.id)
      .pipe(finalize(() => { this.takingOrder = false; }))
      .subscribe({
        next: (response) => {
          this.order.assignedTo = response?.data?.assignedTo ?? null;
          this.toastr.success('Order assigned to you');
        },
        // it failed silently before: the Take button just stayed put
        error: () => this.toastr.error('Unable to take this order'),
      });
  }

}
